import { useState } from 'react';
import { MarkdownRenderer } from '@/components/shared/MarkdownRenderer';
import { formatCurrency, formatPercent } from '@/utils/financial-calculations';
import type { FinancialModelRecord } from '@/types/financial-model';
import FinancialModelTable from './FinancialModelTable';
import FinancialModelCharts from './FinancialModelCharts';

import { useTranslation } from 'react-i18next';
interface FinancialModelDetailPanelProps {
  model: FinancialModelRecord;
  canEdit: boolean;
  canDelete: boolean;
  onEdit: () => void;
  onDelete: () => void;
  onRequestAi: () => void;
  onExportPdf: () => void;
  onExportCsv: () => void;
  isAiLoading?: boolean;
}

type TabType = 'table' | 'charts';

export function FinancialModelDetailPanel({
  model,
  canEdit,
  canDelete,
  onEdit,
  onDelete,
  onRequestAi,
  onExportPdf,
  onExportCsv,
  isAiLoading,
}: FinancialModelDetailPanelProps) {
  const { t, i18n } = useTranslation('financial-model');
  const currencySymbol = i18n.language === 'en' ? '$' : '₽';
  const [activeTab, setActiveTab] = useState<TabType>('table');
  const [tooltipVisible, setTooltipVisible] = useState<string | null>(null);

  const { calculated_data, model_type } = model;
  const { months, summary } = calculated_data;

  const summaryCards = [
    {
      label: t('detail.summaryRevenue'),
      value: `${formatCurrency(summary.totalRevenue)} ${currencySymbol}`,
      color: '#0d0d0d',
    },
    {
      label: t('detail.summaryNetProfit'),
      value: `${formatCurrency(summary.totalNetProfit)} ${currencySymbol}`,
      color: summary.totalNetProfit >= 0 ? '#27ae60' : '#e74c3c',
    },
    {
      label: t('detail.summaryBreakEven'),
      value: summary.breakEvenMonth ? t('detail.breakEvenMonths', { months: summary.breakEvenMonth }) : t('detail.breakEvenNone'),
      color: summary.breakEvenMonth ? '#27ae60' : '#e74c3c',
    },
    {
      label: 'ROI',
      value: formatPercent(summary.roi),
      color: summary.roi >= 0 ? '#27ae60' : '#e74c3c',
    },
  ];

  return (
    <div className="flex-1 min-w-0">
      <div className="bg-[#f4f4f3] rounded-[16px] p-[20px] md:p-[40px] w-full">
        {/* Header */}
        <div className="flex items-start justify-between mb-[24px] gap-[16px]">
          <div className="flex-1 min-w-0">
            <h2 className="font-manrope font-bold text-[24px] md:text-[32px] leading-[32px] md:leading-[40px] text-[#0d0d0d] break-words">
              {model.model_name}
            </h2>
            <p className="font-segoe text-[14px] text-[#565858] mt-[4px]">
              {model_type === 'saas' ? t('wizard.modelSaas.title') : model_type === 'one_time' ? t('wizard.modelOneTime.title') : t('wizard.modelConsulting.title')}
            </p>
          </div>
          <button
            onClick={() => canDelete && onDelete()}
            disabled={!canDelete}
            onMouseEnter={() => !canDelete && setTooltipVisible('delete')}
            onMouseLeave={() => setTooltipVisible(null)}
            className={`relative w-[24px] h-[24px] flex-shrink-0 ${!canDelete ? 'cursor-not-allowed' : 'cursor-pointer hover:opacity-70'} transition-opacity`}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M21 5.98047C17.67 5.65047 14.32 5.48047 10.98 5.48047C9 5.48047 7.02 5.58047 5.04 5.78047L3 5.98047" stroke="#565858" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M8.5 4.97L8.72 3.66C8.88 2.71 9 2 10.69 2H13.31C15 2 15.13 2.75 15.28 3.67L15.5 4.97" stroke="#565858" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M18.85 9.14L18.2 19.21C18.09 20.78 18 22 15.21 22H8.79C6 22 5.91 20.78 5.8 19.21L5.15 9.14" stroke="#565858" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M10.33 16.5H13.66" stroke="#565858" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M9.5 12.5H14.5" stroke="#565858" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            {tooltipVisible === 'delete' && !canDelete && (
              <div className="absolute top-[-60px] left-1/2 -translate-x-1/2 bg-[rgba(13,13,13,0.8)] px-[24px] py-[12px] rounded-[16px] whitespace-nowrap z-10">
                <p className="font-segoe text-[14px] leading-[20px] text-white text-center">
                  {t('tooltip.paidOnlyShort')}
                </p>
              </div>
            )}
          </button>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-[12px] mb-[24px]">
          {summaryCards.map((card) => (
            <div
              key={card.label}
              className="bg-white rounded-[12px] p-[16px] border border-[#eeeeee]"
            >
              <p className="font-segoe text-[13px] text-[#565858] mb-[4px]">
                {card.label}
              </p>
              <p
                className="font-manrope font-bold text-[20px] md:text-[24px] leading-[28px]"
                style={{ color: card.color }}
              >
                {card.value}
              </p>
            </div>
          ))}
        </div>

        {/* Tab Switcher */}
        <div className="flex gap-[4px] mb-[20px] bg-white rounded-[12px] p-[4px] border border-[#eeeeee] w-fit">
          <button
            onClick={() => setActiveTab('table')}
            className={`px-[16px] py-[8px] rounded-[8px] font-segoe text-[14px] transition-colors ${
              activeTab === 'table'
                ? 'bg-[#2197f1] text-white'
                : 'text-[#565858] hover:text-[#0d0d0d]'
            }`}
          >
            {t('detail.tabTable')}
          </button>
          <button
            onClick={() => setActiveTab('charts')}
            className={`px-[16px] py-[8px] rounded-[8px] font-segoe text-[14px] transition-colors ${
              activeTab === 'charts'
                ? 'bg-[#2197f1] text-white'
                : 'text-[#565858] hover:text-[#0d0d0d]'
            }`}
          >
            {t('detail.tabCharts')}
          </button>
        </div>

        {/* Tab Content */}
        <div className="mb-[24px]">
          {activeTab === 'table' ? (
            <FinancialModelTable
              data={months}
              modelType={model_type}
              summary={summary}
            />
          ) : (
            <FinancialModelCharts
              data={months}
              modelType={model_type}
            />
          )}
        </div>

        {/* AI Recommendations */}
        {model.ai_recommendations ? (
          <div className="mb-[24px]">
            <h3 className="font-manrope font-semibold text-[18px] text-[#0d0d0d] mb-[12px]">
              {t('detail.aiRecommendations')}
            </h3>
            <div className="bg-white rounded-[12px] p-[20px] border border-[#eeeeee]">
              <MarkdownRenderer content={model.ai_recommendations} />
            </div>
          </div>
        ) : null}

        {/* Action Buttons */}
        <div className="flex flex-wrap gap-[12px]">
          {/* Edit */}
          <button
            onClick={() => canEdit && onEdit()}
            disabled={!canEdit}
            onMouseEnter={() => !canEdit && setTooltipVisible('edit')}
            onMouseLeave={() => setTooltipVisible(null)}
            className={`relative px-[24px] py-[12px] rounded-[16px] font-segoe font-semibold text-[16px] transition-colors ${
              canEdit
                ? 'bg-[rgba(33,151,241,0.2)] text-[#2197f1] hover:bg-[#2197f1] hover:text-white'
                : 'bg-[rgba(33,151,241,0.2)] text-[#2197f1] cursor-not-allowed opacity-60'
            }`}
          >
            {t('detail.buttonEdit')}
            {tooltipVisible === 'edit' && !canEdit && (
              <div className="absolute top-[-60px] left-1/2 -translate-x-1/2 bg-[rgba(13,13,13,0.8)] px-[24px] py-[12px] rounded-[16px] whitespace-nowrap z-10">
                <p className="font-segoe text-[14px] leading-[20px] text-white text-center">
                  {t('tooltip.paidOnlyShort')}
                </p>
              </div>
            )}
          </button>

          {/* AI Analysis */}
          {!model.ai_recommendations && (
            <button
              onClick={onRequestAi}
              disabled={isAiLoading}
              className={`px-[24px] py-[12px] rounded-[16px] font-segoe font-semibold text-[16px] transition-colors ${
                isAiLoading
                  ? 'bg-[rgba(33,151,241,0.2)] text-[#2197f1] cursor-wait'
                  : 'bg-[#2197f1] text-white hover:bg-[#1a7bc7]'
              }`}
            >
              {isAiLoading ? t('detail.buttonAiAnalyzing') : t('detail.buttonAiAnalyze')}
            </button>
          )}

          {/* Export PDF */}
          <button
            onClick={onExportPdf}
            className="px-[24px] py-[12px] rounded-[16px] bg-[rgba(33,151,241,0.2)] text-[#2197f1] hover:bg-[#2197f1] hover:text-white font-segoe font-semibold text-[16px] transition-colors"
          >
            {t('detail.buttonExportPdf')}
          </button>

          {/* Export CSV */}
          <button
            onClick={onExportCsv}
            className="px-[24px] py-[12px] rounded-[16px] bg-[rgba(33,151,241,0.2)] text-[#2197f1] hover:bg-[#2197f1] hover:text-white font-segoe font-semibold text-[16px] transition-colors"
          >
            {t('detail.buttonExportCsv')}
          </button>
        </div>
      </div>
    </div>
  );
}
